
import React from 'react';

interface Props {
  content: string;
  className?: string;
}

export const SimpleMarkdown: React.FC<Props> = ({ content, className = '' }) => {
  if (!content) return null;

  // 1. Split into lines to handle block elements (lists, paragraphs)
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  
  let currentList: React.ReactNode[] = [];
  let inList = false;

  // Helper to parse inline styles (Bold, Italic)
  const parseInline = (text: string): React.ReactNode[] => {
    // Regex for **bold** and *italic*
    // Note: This is a simple parser. Nested styles might be tricky.
    const parts = text.split(/(\*\*.*?\*\*|\*.*?\*)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
        return <strong key={index} className="font-bold">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('*') && part.endsWith('*') && part.length >= 2) {
        return <em key={index} className="italic">{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    // Check for List Item
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      const listContent = trimmed.substring(2);
      currentList.push(
        <li key={`li-${index}`} className="mb-1">
          {parseInline(listContent)}
        </li>
      );
      inList = true;
    } 
    // Check for Empty Line (Paragraph Break) or End of List
    else {
      if (inList) {
        // Flush the list
        elements.push(
          <ul key={`ul-${index}`} className="list-disc ml-5 mb-3 space-y-1">
            {[...currentList]}
          </ul>
        );
        currentList = [];
        inList = false;
      }

      if (trimmed.length > 0) {
        // Regular Paragraph
        elements.push(
          <p key={`p-${index}`} className="mb-3 last:mb-0 leading-relaxed">
            {parseInline(line)}
          </p>
        );
      }
    }
  });

  // Flush any remaining list at the end
  if (inList) {
    elements.push(
      <ul key={`ul-end`} className="list-disc ml-5 mb-3 space-y-1">
        {[...currentList]}
      </ul>
    );
  }

  return <div className={`markdown-body ${className}`}>{elements}</div>;
};
